import { motion } from "framer-motion";
import {
  Bath,
  BedDouble,
  Building2,
  Bubbles,
  Droplets,
  Flame,
  Home,
  House,
  Leaf,
  Martini,
  Sun,
  Trees,
  Umbrella,
  Users,
  Waves,
  Wind,
} from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { scrollToSection } from "./scroll";
import { useLang } from "@/i18n";

const AIRBNBELO = "M12.001 18.275c-1.353-1.697-2.148-3.184-2.413-4.457-.263-1.027-.16-1.848.291-2.465.477-.71 1.188-1.056 2.121-1.056s1.643.345 2.12 1.063c.446.61.558 1.432.286 2.465-.291 1.298-1.085 2.785-2.412 4.458zm9.601 1.14c-.185 1.246-1.034 2.28-2.2 2.783-2.253.98-4.483-.583-6.392-2.704 3.157-3.951 3.74-7.028 2.385-9.018-.795-1.14-1.933-1.695-3.394-1.695-2.944 0-4.563 2.49-3.927 5.382.37 1.565 1.352 3.343 2.917 5.332-.98 1.085-1.91 1.856-2.732 2.333-.636.344-1.245.558-1.828.609-2.679.399-4.778-2.2-3.825-4.88.132-.345.395-.98.845-1.961l.025-.053c1.464-3.178 3.242-6.79 5.285-10.795l.053-.132.58-1.116c.45-.822.635-1.19 1.351-1.643.346-.21.77-.315 1.246-.315.954 0 1.698.558 2.016 1.007.158.239.345.557.582.953l.558 1.089.08.159c2.041 4.004 3.821 7.608 5.279 10.794l.026.025.533 1.22.318.764c.243.613.294 1.222.213 1.858zm1.22-2.39c-.186-.583-.505-1.271-.9-2.094v-.03c-1.889-4.006-3.642-7.608-5.307-10.844l-.111-.163C15.317 1.461 14.468 0 12.001 0c-2.44 0-3.476 1.695-4.535 3.898l-.081.16c-1.669 3.236-3.421 6.843-5.303 10.847v.053l-.559 1.22c-.21.504-.317.768-.345.847C-.172 20.74 2.611 24 5.98 24c.027 0 .132 0 .265-.027h.372c1.75-.213 3.554-1.325 5.384-3.317 1.829 1.989 3.635 3.104 5.382 3.317h.372c.133.027.239.027.265.027 3.37.003 6.152-3.261 4.802-6.975z";

const SPECS = [
  { beds: "3", guests: "6–8", baths: "2", pool: true, jacuzzi: true, carbet: true, bbq: true, bar: true },
  { beds: "1", guests: "2", baths: "1", pool: true, jacuzzi: true, carbet: true, bbq: true, bar: false },
  { beds: "2", guests: "4–6", baths: "2", pool: false, jacuzzi: false, carbet: false, bbq: true, bar: false },
];

export default function Villa() {
  const { t, isFr } = useLang();

  return (
    <section
      id="villa"
      data-testid="villa-section"
      className="relative py-24 sm:py-32 px-5 sm:px-10"
    >
      <div className="mx-auto max-w-[1500px]">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="clipped-frame">
                <motion.img
                  src="/propriete-nuit.jpg"
                  alt="Villa Mana — architecture et parc tropical"
                  data-testid="villa-photo"
                  whileHover={{ scale: 1.03 }}
                  transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full aspect-[3/4] object-cover"
                />
              </div>
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-sage mt-8">
                {t.villa.labelsTitle}
              </p>
              <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="villa-labels">
                {t.villa.labels.map((label, i) => (
                  <div
                    key={i}
                    data-testid={`villa-label-${i}`}
                    className="flex flex-col items-center justify-center gap-3 py-2 text-center"
                  >
                    <div className="flex items-center justify-center">
                      {i === 0 && (
                        <img
                          src="/images/logements/villa/panonceau-meuble-5etoiles.png"
                          alt="Meublé de tourisme 5 étoiles — panonceau officiel Atout France 2026"
                          className="h-16 w-auto"
                        />
                      )}
                      {i === 1 && (
                        <svg width="27" height="27" viewBox="0 0 24 24" fill="#FF5A5F" role="img" aria-label="Airbnb Superhost">
                          <path d={AIRBNBELO} />
                        </svg>
                      )}
                      {i === 2 && (
                        <div className="w-12 h-12 rounded-full bg-[#FFF] flex items-center justify-center" aria-hidden="true">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="#FF5A5F" role="img" aria-label="Airbnb Coup de cœur des voyageurs">
                            <path d={AIRBNBELO} />
                          </svg>
                        </div>
                      )}
                      {i === 3 && (
                        <img
                          src="/images/logements/villa/odyssea.jpg"
                          alt="Label européen Odyssea — Destinations bleues d'excellence"
                          className="w-28 h-auto rounded-full"
                        />
                      )}
                    </div>
                    {i !== 3 && (
                      <span className="font-mono text-[0.5rem] uppercase tracking-[0.14em] text-sage leading-relaxed">
                        {label}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:pl-8">
            <SectionHeader eyebrow={t.villa.eyebrow} title={t.villa.title} />
            <Reveal delay={0.1}>
              <p className="mt-8 text-sm sm:text-base text-mist font-light leading-relaxed max-w-2xl">
                {t.villa.p1}
              </p>
              <p className="mt-5 text-sm sm:text-base text-mist font-light leading-relaxed max-w-2xl">
                {t.villa.p2}
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div
                data-testid="villa-stats"
                className="mt-12 grid grid-cols-2 sm:grid-cols-4 border-t border-b gold-hairline divide-x divide-y sm:divide-y-0"
                style={{ borderColor: "rgba(194,166,117,0.25)" }}
              >
                {t.villa.stats.map((s, i) => (
                  <div key={i} className="py-6 px-4 text-center" data-testid={`villa-stat-${i}`}>
                    <p className="font-display text-2xl sm:text-3xl text-bone font-light">{s.v}</p>
                    <p className="font-mono text-[0.55rem] uppercase tracking-[0.25em] text-sage mt-2">
                      {s.l}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="flex items-center gap-4 mt-16" data-testid="villa-eco-title">
                <Leaf
                  size={30}
                  strokeWidth={1.5}
                  className="text-[#79A88A] shrink-0 drop-shadow-[0_0_10px_rgba(121,168,138,0.35)]"
                  aria-hidden="true"
                />
                <h3 className="font-display text-2xl sm:text-3xl font-light text-bone">
                  {t.villa.ecoTitle}
                </h3>
              </div>
              <div className="mt-8 grid sm:grid-cols-2 gap-x-10">
                {t.villa.eco.map((e, i) => (
                  <div
                    key={i}
                    data-testid={`villa-eco-${i}`}
                    className="group border-t gold-hairline py-5 hover:bg-moss/40 transition-colors duration-500 px-2 -mx-2"
                  >
                    <div className="flex items-baseline gap-4">
                      <div className="shrink-0">
                        {[Sun, Trees, Wind, Droplets].map((Icon, k) =>
                          k === i ? <Icon key={k} size={18} className="text-gold" aria-hidden="true" /> : null
                        )}
                      </div>
                      <div>
                        <p className="font-display text-xl text-bone">{e.t}</p>
                        <p className="text-xs sm:text-sm text-sage font-light mt-1 leading-relaxed">
                          {e.d}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-20 lg:mt-28">
          <div className="grid md:grid-cols-2 lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-14 items-start">
            <div className="order-1 md:row-start-1 md:col-start-1">
              <SectionHeader eyebrow={t.villa.lodgingTitle} title={t.villa.lodgingTitle} />
            </div>
            <Reveal
              delay={0.2}
              className="order-2 md:col-start-2 md:row-start-1 md:row-span-2"
            >
              <figure
                className="border gold-hairline"
                data-testid="villa-lodging-plan"
              >
                <img
                  src="/images/logements/villa/44-plan-aerien-logements.jpg"
                  alt={
                    isFr
                      ? "La propriété vue du ciel — Le bungalow, La Villa MANA et Le Marina (pas d'accès piscine)"
                      : "The estate from above — The Bungalow, The Villa MANA and The Marina (no pool access)"
                  }
                  className="w-full h-auto block"
                />
                <figcaption className="font-mono text-[0.55rem] uppercase tracking-[0.2em] text-gold px-4 py-3 border-t gold-hairline leading-relaxed">
                  {isFr
                    ? "La propriété vue du ciel — les trois logements"
                    : "The estate from above — the three lodgings"}
                </figcaption>
              </figure>
            </Reveal>
            <Reveal delay={0.1} className="order-3 md:row-start-2 md:col-start-1">
              <div className="mt-6 space-y-5" data-testid="villa-lodging-note">
                {t.villa.lodgingNote.map((p, i) => (
                  <p key={i} className="text-sm sm:text-base text-mist font-light leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-px bg-[rgba(194,166,117,0.25)] border gold-hairline">
            {t.villa.lodgings.map((l, i) => (
              <Reveal
                key={i}
                delay={0.08 * i}
              >
                <button
                  type="button"
                  data-testid={`villa-lodging-card-${i}`}
                  onClick={() => scrollToSection(["lavilla", "bungalow", "marina"][i])}
                  className="h-full w-full text-left bg-night hover:bg-moss/60 transition-colors duration-500 p-8 flex flex-col cursor-pointer"
                >
                  <span className="text-gold">
                    {[Home, House, Building2].map((Icon, k) =>
                      k === i ? <Icon key={k} size={20} aria-hidden="true" /> : null
                    )}
                  </span>
                  <h4 className="font-display text-2xl sm:text-[1.7rem] text-bone mt-4">
                    {l.name}
                  </h4>
                  <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-goldlight mt-3 leading-relaxed">
                    {l.spec}
                  </p>
                  <div
                    className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2"
                    data-testid={`villa-lodging-icons-${i}`}
                  >
                    {[
                      { Icon: BedDouble, v: SPECS[i].beds, on: true, label: isFr ? "chambres" : "bedrooms" },
                      { Icon: Users, v: SPECS[i].guests, on: true, label: isFr ? "personnes" : "guests" },
                      { Icon: Bath, v: SPECS[i].baths, on: true, label: isFr ? "salles de bain" : "bathrooms" },
                      { Icon: Waves, on: SPECS[i].pool, label: isFr ? "accès piscine" : "pool access" },
                      { Icon: Bubbles, on: SPECS[i].jacuzzi, label: isFr ? "accès jacuzzi" : "jacuzzi access" },
                      { Icon: Umbrella, on: SPECS[i].carbet, label: isFr ? "accès carbet" : "carbet access" },
                      { Icon: Flame, on: SPECS[i].bbq, label: isFr ? "barbecue" : "barbecue" },
                      { Icon: Martini, on: SPECS[i].bar, label: isFr ? "bar" : "bar" },
                    ].map(({ Icon, v, on, label }) => (
                      <span key={label} title={label} aria-label={label} className="flex items-center gap-1.5">
                        <Icon size={15} className={on ? "text-gold" : "text-bone/25"} />
                        {v && <span className="font-mono text-[0.6rem] text-bone/70">{v}</span>}
                        {on === false && <span className="font-mono text-[0.55rem] text-bone/30">—</span>}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-sage font-light mt-5 leading-relaxed">
                    {l.d}
                  </p>
                  <span className="mt-6 font-mono text-[0.55rem] uppercase tracking-[0.25em] text-gold">
                    →
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
